import { StackedList } from "@/components/common/StackedList/StackedList"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { isToday } from "date-fns"
import { CheckCheck, Loader2, Settings } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import { Link } from "react-router"
import { NotificationItem } from "../components/NotificationItem"
import { GroupEyebrow } from "../components/NotificationList"
import { CENTER_PAGE_SIZE, NOTIFICATION_SETTINGS_ROUTE } from "../constants"
import { useMarkAllRead } from "../hooks/useMarkRead"
import { useNotifications } from "../hooks/useNotifications"
import { useUnreadCount } from "../hooks/useUnreadCount"
import type { NotificationDTO } from "../types"

/** StackedList's ItemComponent contract (`{ item }`) adapted to NotificationItem's `notification` prop. */
const NotificationStackItem: React.FC<{ item: NotificationDTO }> = ({
  item,
}) => (
  <NotificationItem
    notification={item}
    variant="page"
    ctaEmphasis="primary"
    showDismiss={item.kind === "action_required"}
  />
)

const isCreatedToday = (createdAt: Date | string): boolean => {
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt)
  return !isNaN(date.getTime()) && isToday(date)
}

interface InfiniteScrollSentinelProps {
  /** Skip observing once there's nothing left to fetch, or a fetch is already in flight. */
  enabled: boolean
  onIntersect: () => void
}

/**
 * Invisible marker at the list's tail. IntersectionObserver's default root is
 * the browser viewport, which still works here even though the list scrolls
 * inside MainLayout's own overflow container — scrolling that container moves
 * the sentinel's viewport position just like page scroll would.
 */
const InfiniteScrollSentinel: React.FC<InfiniteScrollSentinelProps> = ({
  enabled,
  onIntersect,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  // The caller passes a fresh `() => fetchNextPage()` closure every render.
  // Reading it through a ref keeps the observer effect keyed on `enabled`
  // only, so the observer isn't torn down/recreated on every parent render.
  const onIntersectRef = useRef(onIntersect)
  useEffect(() => {
    onIntersectRef.current = onIntersect
  }, [onIntersect])

  useEffect(() => {
    if (!enabled) {
      return
    }
    const node = sentinelRef.current
    if (!node) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onIntersectRef.current()
        }
      },
      { rootMargin: "200px" },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled])

  return <div ref={sentinelRef} aria-hidden="true" className="h-px" />
}

/**
 * Single merged list: all notifications, Today/Earlier groups, sorted as
 * served by the API. Action-required items show their CTA (+ "Mark as done")
 * inline; read/acted items stay visible at reduced opacity so nothing
 * silently disappears from the center once it's been dealt with.
 */
const NotificationsList: React.FC = () => {
  const { translate } = useTranslation()
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNotifications({ pageSize: CENTER_PAGE_SIZE })
  const { data: unreadCount } = useUnreadCount()
  const { mutate: markAllRead } = useMarkAllRead()

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  )

  const todayItems = items.filter((item) => isCreatedToday(item.createdAt))
  const earlierItems = items.filter((item) => !isCreatedToday(item.createdAt))
  const count = unreadCount?.count ?? 0

  const renderGroup = (label: string | null, groupItems: NotificationDTO[]) => {
    if (groupItems.length === 0) {
      return null
    }
    return (
      <div className="flex flex-col">
        {label ? <GroupEyebrow label={label} /> : null}
        <StackedList items={groupItems} ItemComponent={NotificationStackItem} />
      </div>
    )
  }

  return (
    <section data-testid="notification-list" className="min-w-0 flex-1">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        {count > 0 ? (
          <span
            data-testid="notification-unread-count"
            className="text-xs text-muted-foreground"
          >
            {translate("NOTIF/UI/UNREAD_COUNT_TEXT", { count })}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          disabled={count === 0}
          onClick={() => markAllRead()}
        >
          <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {translate("NOTIF/UI/MARK_ALL_READ")}
        </Button>
        <Link
          to={NOTIFICATION_SETTINGS_ROUTE}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <Settings className="h-3.5 w-3.5" aria-hidden="true" />
          {translate("NOTIF/SETTINGS/OPEN")}
        </Link>
      </div>

      <div className="flex flex-col pt-2">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {translate("NOTIF/UI/LOADING")}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {translate("NOTIF/UI/EMPTY_CAUGHT_UP")}
          </p>
        ) : (
          <>
            {renderGroup(translate("NOTIF/UI/GROUP_TODAY"), todayItems)}
            {renderGroup(null, earlierItems)}
          </>
        )}
        {hasNextPage ? (
          <InfiniteScrollSentinel
            enabled={!isFetchingNextPage}
            onIntersect={() => fetchNextPage()}
          />
        ) : null}
        {isFetchingNextPage ? (
          <div className="flex items-center justify-center gap-1.5 py-3 text-xs text-muted-foreground">
            <Loader2
              className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
            {translate("NOTIF/UI/LOADING")}
          </div>
        ) : null}
      </div>
    </section>
  )
}

/** Exported for Storybook (renders without the app layout chrome). */
export const NotificationCenterContent: React.FC = () => {
  return (
    <div
      data-agent-target="notifications.center"
      className="flex min-w-0 flex-col gap-6"
    >
      <NotificationsList />
    </div>
  )
}

export const NotificationCenterPage: React.FC = () => {
  const { translate } = useTranslation()
  return (
    <MainLayout
      title={translate("NOTIF/UI/CENTER_TITLE")}
      titleInHeader
      contentCenter={<NotificationCenterContent />}
    />
  )
}
